import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common/Button';
import { useToast } from '../../hooks/useToast';
import { useUpdateProductionStatusMutation, useAssignDeliveryMutation, useGetUserProfilesQuery, usePatchJobMutation, useSendOrderNotificationEmailMutation, useSendInProductionNotificationEmailMutation } from '../../store/slices/apiSlice';
import type { ProductionJobCardProps, ProductionStatus, JobType, ProductionJobSummary, JobPalletType } from '../../types';
import {
  buildDeliveryAssignOptions,
  formatGlassWeightKgForInput,
  formatGlassWeightKgPreviewKg,
  glassWeightKgMatchesJob,
  isPalletDeliverySelection,
  PALLET_TYPE_OPTIONS,
  parseGlassWeightKgInput,
} from '../../utils/palletJobFields';
import { formatCurrencyGBP } from '../../utils/formatNumbers';
import { 
  Factory, AlertTriangle, Settings, ArrowRight, ArrowLeft, Phone, Mail, 
  Calendar, Package, User, Hash, FileText, X, Eye, Send
} from 'lucide-react';
import { StatusChangeModal } from './StatusChangeModal';
import { JobDatesManager } from '../common/JobDatesManager';
import { JobTypeBadge } from '../common/JobTypeBadge';
import {
  getProductionMoveBlockedMessage,
  isInProductionMoveBlocked,
  isProductionLaneMoveBlocked,
  shouldShowUnclassifiedBadge,
} from '../../utils/productionJobHelpers';

// Job type background colors to match the badge colors
const JOB_TYPE_COLORS: Record<JobType, { bgColor: string; textColor: string; borderColor: string }> = {
  ga: {
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-900',
    borderColor: 'border-blue-200',
  },
  gc: {
    bgColor: 'bg-green-50',
    textColor: 'text-green-900',
    borderColor: 'border-green-200',
  },
  gap: {
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-900',
    borderColor: 'border-purple-200',
  },
};

// Quotation Product: when any order has WooCommerce "Quotation Product"
const QUOTATION_COLORS = {
  bgColor: 'bg-amber-50',
  textColor: 'text-amber-900',
  borderColor: 'border-amber-200',
};

const STATUS_CONFIG: Record<ProductionStatus, { 
  label: string; 
  icon: React.ComponentType<any>; 
  color: string; 
  bgColor: string;
  textColor: string;
}> = {
  new_orders: {
    label: 'New Orders',
    icon: Factory,
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
  },
  problem_orders: {
    label: 'Problem Orders', 
    icon: AlertTriangle,
    color: 'red',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
  },
  in_production: {
    label: 'In Production',
    icon: Settings,
    color: 'green', 
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
  },
};

// Order matches tab order: New, In, Problem
const MOVE_OPTIONS: Record<ProductionStatus, ProductionStatus[]> = {
  new_orders: ['in_production', 'problem_orders'],
  in_production: ['new_orders', 'problem_orders'],
  problem_orders: ['new_orders', 'in_production'],
};

// Button colors: problem=red, in=blue, new=secondary
const MOVE_BUTTON_VARIANT: Record<ProductionStatus, 'primary' | 'secondary' | 'danger'> = {
  new_orders: 'secondary',
  in_production: 'primary',
  problem_orders: 'danger',
};

// Status order for arrow direction (matches tab order: New, In, Problem)
const STATUS_ORDER: Record<ProductionStatus, number> = {
  new_orders: 0,
  in_production: 1,
  problem_orders: 2,
};

/** Show Ordered & Checked section for GA, GAP, GC, and Quotation jobs in New Orders tab */
function shouldShowOrderedAndChecked(job: ProductionJobSummary): boolean {
  if (job.production_status !== 'new_orders') return false;
  return (
    job.job_type === 'ga' ||
    job.job_type === 'gap' ||
    job.job_type === 'gc' ||
    job.has_quotation_product === true
  );
}

/** Review / final order emails are not used for GC; still show toggle for dates gating. */
function shouldShowOrderCheckEmails(job: ProductionJobSummary): boolean {
  return job.job_type !== 'gc';
}

const PALLET_DETAILS_REQUIRED_FOR_MOVE_MESSAGE =
  'Set Pallet Type and Glass Weight first';

function isSavedGlassWeightKgMissing(job: ProductionJobSummary): boolean {
  const v = job.glass_weight_kg;
  if (v === null || v === undefined) return true;
  if (typeof v === 'string' && String(v).trim() === '') return true;
  if (typeof v === 'number' && (Number.isNaN(v) || v <= 0)) return true;
  return false;
}

/** New Orders + pallet assignee: block lane moves until pallet type and weight are saved. */
function isNewOrdersPalletMoveBlockedByDetails(
  job: ProductionJobSummary,
  isPalletAssign: boolean,
  targetStatus: ProductionStatus
): boolean {
  if (job.production_status !== 'new_orders' || !isPalletAssign) return false;
  if (targetStatus !== 'in_production' && targetStatus !== 'problem_orders') return false;
  const preset = (job.pallet_type as JobPalletType | undefined) ?? 'no_preset';
  return preset === 'no_preset' || isSavedGlassWeightKgMissing(job);
}

type InProductionValidation = {
  valid: boolean;
  missingFields: string[];
  message: string;
};

/** Validate required dates + assigned engineer before moving to In Production. */
function validateInProductionRequirements(job: ProductionJobSummary): InProductionValidation {
  const missing: string[] = [];
  const needsGlassArrival =
    job.job_type !== 'gc' &&
    (job.job_type === 'ga' ||
      job.job_type === 'gap' ||
      job.has_quotation_product === true);

  if (needsGlassArrival && !job.glass_arrival_date) {
    missing.push('Glass Arrival Date');
  }
  if (!job.manufacture_date) {
    missing.push('Date of Manufacture');
  }
  if (!job.potential_delivery_date) {
    missing.push('Potential Delivery Date');
  }
  if (!job.delivery_user) {
    missing.push('Assigned Engineer');
  }

  if (missing.length === 0) {
    return { valid: true, missingFields: [], message: '' };
  }

  const message =
    missing.length === 1
      ? `${missing[0]} must be set before moving to In Production. Please scroll down to fill in the required field.`
      : `The following must be set before moving to In Production: ${missing.join(', ')}. Please scroll down to fill in the required fields.`;

  return { valid: false, missingFields: missing, message };
}

export const ProductionJobCard: React.FC<ProductionJobCardProps> = ({
  job,
  onStatusChange,
  onViewOrders
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [updateStatus, { isLoading }] = useUpdateProductionStatusMutation();
  const [assignDelivery, { isLoading: isAssigning }] = useAssignDeliveryMutation();
  const [patchJob, { isLoading: isJobPatching }] = usePatchJobMutation();
  const [sendReviewEmail, { isLoading: isSendingReview }] = useSendOrderNotificationEmailMutation();
  const [sendOrderEmail, { isLoading: isSendingOrder }] = useSendOrderNotificationEmailMutation();
  const [sendInProductionEmail] = useSendInProductionNotificationEmailMutation();
  const { data: userProfiles } = useGetUserProfilesQuery({ page: 1, page_size: 100, ordering: 'user__username' });
  
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    targetStatus?: ProductionStatus;
  }>({ isOpen: false });
  const [selectedDeliveryUser, setSelectedDeliveryUser] = useState<string>('');
  const [productionNotes, setProductionNotes] = useState<string>(job.production_notes || '');
  const [highlightDates, setHighlightDates] = useState<boolean>(false);
  const [palletType, setPalletType] = useState<JobPalletType>(
    (job.pallet_type as JobPalletType | undefined) ?? 'no_preset'
  );
  const [glassWeightInput, setGlassWeightInput] = useState<string>(
    formatGlassWeightKgForInput(job.glass_weight_kg)
  );
  const [smallVan, setSmallVan] = useState<boolean>(job.small_van ?? false);

  // Set initial delivery user selection
  React.useEffect(() => {
    if (job.delivery_user && !selectedDeliveryUser) {
      setSelectedDeliveryUser(String(job.delivery_user));
    }
  }, [job.delivery_user, selectedDeliveryUser]);

  // Update production notes when job.production_notes changes
  React.useEffect(() => {
    setProductionNotes(job.production_notes || '');
  }, [job.production_notes]);

  React.useEffect(() => {
    setPalletType((job.pallet_type as JobPalletType | undefined) ?? 'no_preset');
    setGlassWeightInput(formatGlassWeightKgForInput(job.glass_weight_kg));
    setSmallVan(job.small_van ?? false);
  }, [job.id, job.pallet_type, job.glass_weight_kg, job.small_van]);

  const handleSavePalletDetails = async () => {
    try {
      const updatedJob = await patchJob({
        id: job.id,
        data: {
          pallet_type: palletType,
          glass_weight_kg: parseGlassWeightKgInput(glassWeightInput),
          small_van: smallVan,
        },
      }).unwrap();
      setPalletType((updatedJob.pallet_type as JobPalletType | undefined) ?? 'no_preset');
      setGlassWeightInput(formatGlassWeightKgForInput(updatedJob.glass_weight_kg));
      setSmallVan(updatedJob.small_van ?? false);
      toast.success('Pallet details updated', 'Pallet fields have been saved');
    } catch (error: unknown) {
      const message =
        typeof error === 'object' && error !== null && 'data' in error
          ? (error as { data?: { detail?: string } }).data?.detail
          : undefined;
      toast.error('Update Failed', message || 'Please try again');
    }
  };

  const handleUpdateProductionNotes = async () => {
    const notesToSave = productionNotes.trim() || null;
    
    try {
      const updatedJob = await patchJob({ 
        id: job.id, 
        data: { production_notes: notesToSave }
      }).unwrap();
      
      // Force local state to match what was actually saved
      setProductionNotes(updatedJob.production_notes || '');
      
      toast.success('Production Notes Updated', 'Notes have been updated successfully');
    } catch (error: any) {
      toast.error('Update Failed', error?.data?.detail || 'Please try again');
      // Revert local state on error
      setProductionNotes(job.production_notes || '');
    }
  };

  const handleClearProductionNotes = async () => {
    try {
      await patchJob({ 
        id: job.id, 
        data: { production_notes: null }
      }).unwrap();
      
      // Force local state to match cleared state
      setProductionNotes('')
      
      toast.success('Production Notes Cleared', 'Notes have been cleared successfully');
    } catch (error: any) {
      toast.error('Clear Failed', error?.data?.detail || 'Please try again');
      // Revert local state on error
      setProductionNotes(job.production_notes || '');
    }
  };

  const statusConfig = STATUS_CONFIG[job.production_status as ProductionStatus];
  const StatusIcon = statusConfig.icon;
  const moveOptions = MOVE_OPTIONS[job.production_status as ProductionStatus];
  const datesReadOnlyUntilOrderCheck =
    shouldShowOrderedAndChecked(job) && !job.ordered_and_checked;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getArrowDirection = (fromStatus: ProductionStatus, toStatus: ProductionStatus) => {
    const fromOrder = STATUS_ORDER[fromStatus];
    const toOrder = STATUS_ORDER[toStatus];
    return toOrder > fromOrder ? ArrowRight : ArrowLeft;
  };

  const deliveryOptions = React.useMemo(
    () => buildDeliveryAssignOptions(userProfiles),
    [userProfiles]
  );

  const effectiveDeliveryUserId =
    selectedDeliveryUser || (job.delivery_user != null ? String(job.delivery_user) : '');

  const isPalletAssign = React.useMemo(
    () =>
      isPalletDeliverySelection(
        effectiveDeliveryUserId,
        deliveryOptions,
        job.delivery_user_username
      ),
    [effectiveDeliveryUserId, deliveryOptions, job.delivery_user_username]
  );

  const showPalletFields =
    job.production_status === 'new_orders' && isPalletAssign;

  const palletFieldsDirty =
    palletType !== ((job.pallet_type as JobPalletType | undefined) ?? 'no_preset') ||
    !glassWeightKgMatchesJob(job.glass_weight_kg, glassWeightInput) ||
    smallVan !== (job.small_van ?? false);

  const handleDeliveryUserChange = async (userId: string) => {
    setSelectedDeliveryUser(userId);
    if (!userId) return;

    try {
      await assignDelivery({
        id: job.id,
        data: { delivery_user: parseInt(userId) },
      }).unwrap();
      toast.success('Delivery Assigned', 'Assigned engineer has been saved');
    } catch (error: any) {
      toast.error('Assignment Failed', error?.data?.detail || 'Please try again');
      setSelectedDeliveryUser(job.delivery_user ? String(job.delivery_user) : '');
    }
  };

  const handleMoveClick = (targetStatus: ProductionStatus) => {
    if (isNewOrdersPalletMoveBlockedByDetails(job, isPalletAssign, targetStatus)) {
      toast.warning('Missing pallet details', PALLET_DETAILS_REQUIRED_FOR_MOVE_MESSAGE);
      return;
    }
    if (isInProductionMoveBlocked(job, targetStatus)) {
      toast.warning('Move not available', getProductionMoveBlockedMessage(job, targetStatus));
      return;
    }
    if (isProductionLaneMoveBlocked(job, targetStatus)) {
      toast.warning('Move not available', getProductionMoveBlockedMessage(job, targetStatus));
      return;
    }
    if (
      targetStatus === 'in_production' &&
      (job.production_status === 'new_orders' || job.production_status === 'problem_orders')
    ) {
      const validation = validateInProductionRequirements(job);
      if (!validation.valid) {
        toast.error('Missing Required Fields', validation.message);
        const hasDateMissing = validation.missingFields.some(
          (f) =>
            f === 'Glass Arrival Date' ||
            f === 'Date of Manufacture' ||
            f === 'Potential Delivery Date'
        );
        if (hasDateMissing) {
          setHighlightDates(true);
          setTimeout(() => setHighlightDates(false), 5000);
        }
        return;
      }
    }

    setModalConfig({
      isOpen: true,
      targetStatus,
    });
  };

  const handleStatusChange = async (payload: {
    notes?: string;
    useActualManufactureDateInEmail?: boolean;
  }) => {
    const notes = payload.notes;
    if (!modalConfig.targetStatus) return;

    if (isInProductionMoveBlocked(job, modalConfig.targetStatus)) {
      toast.warning('Move not available', getProductionMoveBlockedMessage(job, modalConfig.targetStatus));
      setModalConfig({ isOpen: false });
      return;
    }

    if (isProductionLaneMoveBlocked(job, modalConfig.targetStatus)) {
      toast.warning('Move not available', getProductionMoveBlockedMessage(job, modalConfig.targetStatus));
      setModalConfig({ isOpen: false });
      return;
    }

    if (isNewOrdersPalletMoveBlockedByDetails(job, isPalletAssign, modalConfig.targetStatus)) {
      toast.warning('Missing pallet details', PALLET_DETAILS_REQUIRED_FOR_MOVE_MESSAGE);
      setModalConfig({ isOpen: false });
      return;
    }

    if (
      modalConfig.targetStatus === 'in_production' &&
      (job.production_status === 'new_orders' || job.production_status === 'problem_orders')
    ) {
      const validation = validateInProductionRequirements(job);
      if (!validation.valid) {
        toast.error('Missing Required Fields', validation.message);
        return;
      }
    }

    const targetStatus = modalConfig.targetStatus;

    try {
      await updateStatus({
        id: job.id,
        production_status: targetStatus
      }).unwrap();

      toast.success(
        'Status Updated',
        `Job moved to ${STATUS_CONFIG[targetStatus].label}`
      );
      setModalConfig({ isOpen: false });
      onStatusChange?.(job.id, targetStatus);

      void (async () => {
        if (notes !== undefined) {
          try {
            await patchJob({
              id: job.id,
              data: { production_notes: notes || null }
            }).unwrap();
            toast.success('Notes saved', 'Production notes were updated');
          } catch (noteError: any) {
            toast.error(
              'Notes update failed',
              noteError?.data?.detail || 'Status changed but notes could not be saved. Please try again from the job.'
            );
          }
        }

        if (targetStatus === 'in_production' && job.customer_email) {
          try {
            await sendInProductionEmail({
              job_id: job.id,
              use_actual_manufacture_date: payload.useActualManufactureDateInEmail === true,
            }).unwrap();
            toast.success('Email Queued', 'In-production notification email has been queued for delivery');
          } catch (emailError: any) {
            const errorMessage = emailError?.data?.error || emailError?.data?.message || 'Failed to send notification email';
            toast.warning('Email Failed', errorMessage);
            console.error('Failed to send in-production email:', emailError);
          }
        }
      })();
    } catch (error: any) {
      toast.error(
        'Update Failed',
        error?.data?.detail || 'Please try again'
      );
    }
  };

  // Get job type colors for the header background (Quotation overrides when present)
  const getJobTypeColors = () => {
    if (job.has_quotation_product) {
      return QUOTATION_COLORS;
    }
    if (job.job_type && JOB_TYPE_COLORS[job.job_type]) {
      return JOB_TYPE_COLORS[job.job_type];
    }
    return {
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-900',
      borderColor: 'border-gray-200',
    };
  };

  const jobTypeColors = getJobTypeColors();

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        {/* Header: Icon + Customer Name + Postcode + Actions */}
        <div className={`p-4 border-b ${jobTypeColors.borderColor} ${jobTypeColors.bgColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <StatusIcon className={`w-5 h-5 ${statusConfig.textColor}`} />
              <div className="flex items-center space-x-3">
                <span 
                  className={`font-semibold ${jobTypeColors.textColor} cursor-pointer hover:underline`}
                  onClick={() => navigate(`/jobs/${job.id}/edit`)}
                >
                  {job.customer_name}
                </span>
                {job.website_job_number ? (
                  <span className="text-sm font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    #{job.website_job_number}
                  </span>
                ) : null}
                <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                  {job.delivery_postcode}
                </span>
                {job.priority !== 'normal' && (
                  <span className={`
                    inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                    ${job.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                      job.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-800'}
                  `}>
                    {job.priority.toUpperCase()}
                  </span>
                )}
                {job.has_quotation_product && (
                  <span
                    className="inline-flex items-center rounded-full font-medium border px-2.5 py-1 text-sm bg-amber-50 text-amber-700 border-amber-200"
                    title="Job contains at least one Quotation Product order"
                  >
                    Quotation
                  </span>
                )}
              </div>
            </div>
            
            {/* CENTER: Job Type Badge (GA / GC / GAP) */}
            <div className="flex-1 flex justify-center items-center gap-2 min-w-0">
              <JobTypeBadge jobType={job.job_type} size="md" />
              {shouldShowUnclassifiedBadge(job) && (
                <span
                  className="inline-flex items-center rounded-full font-medium border px-2.5 py-1 text-sm bg-gray-100 text-gray-700 border-gray-300"
                  title="Assign glass values on job orders to classify as GA, GAP, or GC"
                >
                  Unclassified
                </span>
              )}
            </div>
            
            {/* Action Buttons in Header */}
            <div className="flex items-center gap-2">
              {moveOptions.map((targetStatus) => {
                const targetConfig = STATUS_CONFIG[targetStatus];
                const ArrowIcon = getArrowDirection(job.production_status as ProductionStatus, targetStatus);
                const buttonVariant = MOVE_BUTTON_VARIANT[targetStatus];
                const blockedFromInProduction = isInProductionMoveBlocked(job, targetStatus);
                const blockedFromProductionLane =
                  targetStatus === 'in_production'
                    ? blockedFromInProduction
                    : isProductionLaneMoveBlocked(job, targetStatus);
                const blockedFromPalletDetails = isNewOrdersPalletMoveBlockedByDetails(
                  job,
                  isPalletAssign,
                  targetStatus
                );
                const moveTitle = blockedFromPalletDetails
                  ? PALLET_DETAILS_REQUIRED_FOR_MOVE_MESSAGE
                  : blockedFromProductionLane
                    ? getProductionMoveBlockedMessage(job, targetStatus)
                    : undefined;
                const isNewOrdersPrimaryMove =
                  job.production_status === 'new_orders' && targetStatus === 'in_production';

                return (
                  <Button
                    key={targetStatus}
                    size={isNewOrdersPrimaryMove ? 'md' : 'sm'}
                    variant={buttonVariant}
                    disabled={isLoading || blockedFromProductionLane || blockedFromPalletDetails}
                    title={moveTitle}
                    onClick={() => handleMoveClick(targetStatus)}
                    className={
                      isNewOrdersPrimaryMove
                        ? 'text-sm whitespace-nowrap'
                        : 'text-xs whitespace-nowrap'
                    }
                  >
                    <div className="flex items-center space-x-1">
                      <ArrowIcon className={isNewOrdersPrimaryMove ? 'w-4 h-4' : 'w-3 h-3'} />
                      <span>Move to {targetConfig.label.replace(' Orders', '').replace(' Production', '')}</span>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content - Balanced Column Layout */}
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4">
            {/* Left Section: Customer & Job Details */}
            <div className="md:col-span-1 lg:col-span-2 space-y-3">
              {/* Customer Information */}
              <div>
                <div className="space-y-3">
                  {/* Customer Details */}
                  <div className="space-y-2">
                    <div className="font-semibold text-gray-900 text-base">
                      {job.customer_name}
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <Mail className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                      <span className="text-base">{job.customer_email || 'No email provided'}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <Phone className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                      <span className="text-base">{job.customer_mobile_number || 'No phone provided'}</span>
                    </div>
                  </div>

                  {/* Job Information */}
                  <div className="pt-4 border-t border-gray-200 space-y-2">
                    {job.website_job_number && (
                      <div className="flex items-center">
                        <Hash className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0" />
                        <div>
                          <span className="text-sm text-gray-500">Website Order:</span>
                          <span className="ml-2 font-mono text-sm font-semibold text-blue-700">{job.website_job_number}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <Package className="w-4 h-4 mr-2 text-purple-500 flex-shrink-0" />
                      <div>
                        <span className="text-sm text-gray-500">Items:</span>
                        <span className="ml-2 text-sm font-semibold text-gray-900">{job.total_items}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <span className="text-green-600 font-semibold text-sm mr-2">£</span>
                      <div>
                        <span className="text-sm text-gray-500">Total Amount:</span>
                        <span className="ml-2 text-sm font-semibold text-green-600">
                          {formatCurrencyGBP(job.total_amount).replace(/^£/, '')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Created/By Information */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>Created {formatDate(job.created_at)}</span>
                      </div>
                      {job.created_by_username && (
                        <div className="flex items-center space-x-1">
                          <User className="w-3 h-3" />
                          <span>by {job.created_by_username}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* In-Production Email Status Indicator */}
                  {job.production_status === 'in_production' && job.in_production_email_sent_at && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                        <div className="flex items-start space-x-2">
                          <div className="flex-shrink-0">
                            <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-green-800">In-Production Email Sent</p>
                            <p className="text-xs text-green-700 mt-1">
                              by {job.in_production_email_sent_by_username || 'System'}
                            </p>
                            <p className="text-xs text-green-600 mt-0.5">
                              {new Date(job.in_production_email_sent_at).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })} at {new Date(job.in_production_email_sent_at).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Check Section (GA/GAP/GC/Quotation in New Orders) */}
            <div className="md:col-span-1 lg:col-span-2">
              {shouldShowOrderedAndChecked(job) ? (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Ordered and Checked</h4>
                  
                  <div className="bg-indigo-50 rounded-lg p-3 space-y-3 border border-indigo-200">
                    {/* Toggle Switch */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-700">Ordered and Checked</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={job.ordered_and_checked || false}
                            onChange={async (e) => {
                              try {
                                await patchJob({
                                  id: job.id,
                                  data: { ordered_and_checked: e.target.checked }
                                }).unwrap();
                                toast.success(
                                  e.target.checked ? 'Unchecked' : 'Checked',
                                  e.target.checked 
                                    ? 'Order marked as unchecked' 
                                    : 'Order marked as checked'
                                );
                              } catch (error: any) {
                                toast.error('Update Failed', error?.data?.detail || 'Please try again');
                              }
                            }}
                            disabled={isJobPatching}
                            className="sr-only"
                          />
                          <div className={`
                            relative w-11 h-6 rounded-full transition-colors duration-200
                            ${job.ordered_and_checked 
                              ? 'bg-green-500' 
                              : 'bg-gray-300'
                            }
                            ${isJobPatching ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          `}>
                            <div className={`
                              absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200
                              ${job.ordered_and_checked ? 'translate-x-5' : 'translate-x-0'}
                            `} />
                          </div>
                        </label>
                      </div>
                      
                      {/* Show user and timestamp when checked */}
                      {job.ordered_and_checked && job.ordered_checked_by_username && job.ordered_checked_at && (
                        <div className="text-xs text-gray-600 pt-1">
                          by {job.ordered_checked_by_username} at {new Date(job.ordered_checked_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })} {new Date(job.ordered_checked_at).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      )}
                    </div>

                    {/* Email Buttons - GA/GAP/Quotation only; not applicable for GC */}
                    {job.ordered_and_checked && shouldShowOrderCheckEmails(job) && (
                      <div className="space-y-2 pt-2 border-t border-indigo-200">
                        {/* Send Review Email Button */}
                        <div className="space-y-1">
                        <Button
                          size="sm"
                          variant={job.review_email_sent_at ? "secondary" : "primary"}
                          onClick={async () => {
                            try {
                              await sendReviewEmail({
                                job_id: job.id,
                                email_type: 'review'
                              }).unwrap();
                              toast.success('Review Email Queued', 'Review email has been queued for delivery');
                            } catch (error: any) {
                              const errorMessage = error?.data?.error || error?.data?.message || 'Failed to send review email';
                              toast.error('Email Failed', errorMessage);
                            }
                          }}
                          disabled={isSendingReview || !job.customer_email}
                          className="w-full text-xs"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {isSendingReview ? 'Sending...' : (job.review_email_sent_at ? 'Resend Review Email' : 'Send Review Email')}
                        </Button>
                        {job.review_email_sent_at && job.review_email_sent_by_username && (
                          <div className="text-xs text-gray-600">
                            by {job.review_email_sent_by_username} at {new Date(job.review_email_sent_at).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })} {new Date(job.review_email_sent_at).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </div>
                        )}
                      </div>

                      {/* Send Order Email Button */}
                      <div className="space-y-1">
                        <Button
                          size="sm"
                          variant={job.order_email_sent_at ? "secondary" : "primary"}
                          onClick={async () => {
                            try {
                              await sendOrderEmail({
                                job_id: job.id,
                                email_type: 'final'
                              }).unwrap();
                              toast.success('Order Email Queued', 'Order email has been queued for delivery');
                            } catch (error: any) {
                              const errorMessage = error?.data?.error || error?.data?.message || 'Failed to send order email';
                              toast.error('Email Failed', errorMessage);
                            }
                          }}
                          disabled={isSendingOrder || !job.customer_email}
                          className="w-full text-xs"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {isSendingOrder ? 'Sending...' : (job.order_email_sent_at ? 'Resend Order Email' : 'Send Order Email')}
                        </Button>
                        {job.order_email_sent_at && job.order_email_sent_by_username && (
                          <div className="text-xs text-gray-600">
                            by {job.order_email_sent_by_username} at {new Date(job.order_email_sent_at).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })} {new Date(job.order_email_sent_at).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </div>
                        )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              
              {/* View Orders Button - Outside the card, on all tabs */}
              {onViewOrders && (
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onViewOrders(job.id, job.customer_name)}
                    className="w-full"
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <Eye className="w-4 h-4" />
                      <span>View Orders</span>
                    </div>
                  </Button>
                </div>
              )}
            </div>

            {/* Center Section: Delivery Assignment & Production Notes */}
            <div className="md:col-span-1 lg:col-span-2">
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Assign Delivery</h4>
                
                <div className="bg-blue-50 rounded-lg p-3">
                  <select
                    className="form-input text-sm w-full bg-gradient-to-r from-blue-50 to-green-50 border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                    value={selectedDeliveryUser || (job.delivery_user ? String(job.delivery_user) : '')}
                    onChange={(e) => handleDeliveryUserChange(e.target.value)}
                    disabled={isAssigning}
                  >
                    <option value="">
                      {deliveryOptions.length > 0 ? 'Select delivery user…' : 'No Collection & Delivery users available'}
                    </option>
                    {deliveryOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label} • [{opt.role}]
                      </option>
                    ))}
                  </select>
                </div>

                {showPalletFields && (
                  <div className="rounded-lg border border-blue-100 bg-white p-3 space-y-3 text-sm">
                    <p className="text-xs font-medium text-gray-600">Pallet delivery details</p>
                    <div>
                      <label className="block text-gray-700 mb-1">Pallet Type</label>
                      <select
                        className="form-input w-full"
                        value={palletType}
                        onChange={(e) => setPalletType(e.target.value as JobPalletType)}
                      >
                        {PALLET_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-1">Glass Weight</label>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          value={glassWeightInput}
                          onChange={(e) => setGlassWeightInput(e.target.value)}
                          placeholder="e.g. 5.67"
                          className="form-input flex-1 min-w-[8rem] max-w-[12rem]"
                        />
                        <span className="text-gray-500 text-xs sm:text-sm">
                          Preview:{' '}
                          {formatGlassWeightKgPreviewKg(parseGlassWeightKgInput(glassWeightInput))}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-1">Small van</label>
                      <select
                        className="form-input w-full"
                        value={smallVan ? 'yes' : 'no'}
                        onChange={(e) => setSmallVan(e.target.value === 'yes')}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleSavePalletDetails}
                      disabled={isJobPatching || !palletFieldsDirty}
                      className="w-full"
                    >
                      Update pallet details
                    </Button>
                  </div>
                )}

                {/* Production Notes Section */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Production Notes</h4>
                  <div className="bg-yellow-50 rounded-lg p-3 space-y-2">
                    <textarea
                      value={productionNotes}
                      onChange={(e) => setProductionNotes(e.target.value)}
                      placeholder="Notes for throughout production..."
                      rows={3}
                      className="form-input text-sm w-full resize-none focus:ring-yellow-500 focus:border-yellow-500"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleUpdateProductionNotes}
                        disabled={isJobPatching || productionNotes.trim() === (job.production_notes || '')}
                        className="flex-1"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Update
                      </Button>
                      {job.production_notes && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleClearProductionNotes}
                          disabled={isJobPatching}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Right Section: Production Dates */}
            <div className={`md:col-span-2 lg:col-span-2 transition-all duration-300 ${
              highlightDates 
                ? 'ring-4 ring-red-400 ring-opacity-50 rounded-lg p-2 -m-2 bg-red-50' 
                : ''
            }`}>
              <JobDatesManager
                jobId={job.id}
                hideGlassArrival={job.job_type === 'gc'}
                fulfillmentType={job.fulfillment_type}
                assignedDeliveryUserRole={(() => {
                  if (job.delivery_user) {
                    const assignedUser = userProfiles?.results?.find(p => p.user === job.delivery_user);
                    return assignedUser?.role_details?.name || null;
                  }
                  return null;
                })()}
                currentDates={{
                  glass_arrival_date: job.glass_arrival_date,
                  manufacture_date: job.manufacture_date,
                  potential_delivery_date: job.potential_delivery_date
                }}
                lockData={job.lock_data}
                datesReadOnly={datesReadOnlyUntilOrderCheck}
                datesReadOnlyHint="Turn on Ordered and Checked to edit production dates."
                onDatesUpdate={() => {
                  // The RTK Query cache will handle the actual data update
                  // Could add local state update here if needed
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <StatusChangeModal
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig({ isOpen: false })}
        onConfirm={handleStatusChange}
        currentStatus={job.production_status}
        targetStatus={modalConfig.targetStatus!}
        jobInfo={{
          id: job.id,
          customer_name: job.customer_name,
          website_job_number: job.website_job_number || undefined,
          production_notes: job.production_notes
        }}
        isLoading={isLoading}
      />
    </>
  );
};
